import { toast } from "@gigpro/ui/Toast";
import { draftsAtomFor, dropCancelledHandPosts } from "domains/aiChat/lib/drafts";
import { conversationIdAtom } from "domains/aiChat/lib/storage";
import { atom, useAtomValue, useSetAtom, useStore } from "jotai";
import { atomWithStorage } from "jotai/utils";
import React from "react";
import { safeJSONStorage } from "utils/safeStorage";
import { anySentRole, changesSentRoles, keepsSentRoles, sentLockOf } from "./counts";
import { withGivenFields } from "./defaults";
import { withHandSet } from "./hand-set";
import { currentDraft, dayLabel, emptyDraft, isEmptyDraft, isLivePost, type PostDraft, todayIn } from "./model";
import type { PagePhase } from "./post-phase";
import { revisionOf } from "./revision";

const chatEditToasts = new Set<string | number>();

/** Remembers a chat edit's Undo toast, so the receipt can dismiss those and no other toast on screen. */
export const rememberChatEditToast = (id: string | number) => {
  chatEditToasts.add(id);
  return id;
};

export const dismissChatEditToasts = () => {
  for (const id of chatEditToasts) toast.dismiss(id);
  chatEditToasts.clear();
};

/** One draft per manager and location, `"<user id>:<location id>"`: kept across reloads, leaving the page and switching location. */
export const draftKeyOf = (userId: number | undefined, locationId: number) => `${userId ?? 0}:${locationId}`;

const DRAFT_KEY = /^\d+:\d+$/;
const DAY_MS = 24 * 60 * 60 * 1000;
/** A draft nobody touched for this long is gone, even if its day has not come. */
const DRAFT_LIFETIME_MS = 7 * DAY_MS;
/** A draft touched within this is still the manager's task in hand: coming back to it needs no question. */
export const RESUME_WINDOW_MS = 30 * 60 * 1000;

/** A draft is gone once its earliest shift day has passed, otherwise once it has sat for a week. */
export const draftExpired = (draft: PostDraft, now: number) => {
  const today = todayIn(null, new Date(now));
  if (draft.shifts.some((shift) => !!shift.date && shift.date < today)) return true;
  return !!draft.saved_at && now - draft.saved_at > DRAFT_LIFETIME_MS;
};

/** Drafts of other keys (from before drafts were per manager) and expired ones are dropped, never resumed; older ones are read the current way. */
const liveDrafts = (all: Record<string, PostDraft>, now: number) =>
  Object.fromEntries(
    Object.entries(all)
      .filter(([key, draft]) => DRAFT_KEY.test(key) && !draftExpired(draft, now))
      .map(([key, draft]) => [key, currentDraft(draft)]),
  );

/** What a new intent is offered about an unfinished draft: when it was last touched, and how to settle it. */
export const unfinishedAtom = atom<{
  savedAt: number;
  /** "Continue draft · Fri, Oct 16 · Prep Cook": what the draft is, as every surface names it. */
  label: string;
  resolve: (choice: "continue" | "new") => void;
} | null>(null);

export const postDraftsAtom = atomWithStorage<Record<string, PostDraft>>(
  "qwick-post-page-drafts",
  {},
  safeJSONStorage<Record<string, PostDraft>>(),
  {
    getOnInit: true,
  },
);

/** Who made a change: the hand edits collapse into one status line for the chat; the chat's own are its own words. */
export type EditSource = "hand" | "chat";
/** What a note says it came from: an edit by hand or by the chat, or the page itself (a post that ran, failed or clashed). */
export type NoteSource = EditSource | "page";

export type UndoEntry = { label: string; before: PostDraft };
export type EditNote = { source: NoteSource; field: string; card_id: string; text: string; at: number; after: number };

/** Where the posting page is (editing, posting, stopped, posted), for the chat beside it: a posted page offers no next step. */
export const postPhaseAtom = atom<PagePhase>("editing");
/** The chat's Post page's posting session, sent with every chat request while it is open; null elsewhere. */
export const postingSessionAtom = atom<string | null>(null);
/** Whether Review is open on the posting page: a pill that opens it is dead then. */
export const reviewOpenAtom = atom(false);

/** How many chat messages there are: a hand edit is said after the message it followed. Written by the chat pane. */
export const chatMessageCountAtom = atom(0);

const MAX_UNDO = 50;
const MAX_NOTES = 8;

/**
 * A line the page says in the chat (a post's outcome, a clash, a chip the manager used), kept with its conversation: the
 * open one unless the caller names another, or none.
 */
export const sayOnPage = (
  store: ReturnType<typeof useStore>,
  locationKey: string,
  text: string,
  field: string,
  conversationId: string | null = store.get(conversationIdAtom),
) => {
  const note: EditNote = {
    source: "page",
    field,
    card_id: "",
    text,
    at: Date.now(),
    after: store.get(chatMessageCountAtom),
  };
  store.set(editNotesAtom, (all) => ({ ...all, [locationKey]: pushNote(all[locationKey] ?? [], note) }));
  const line: PageLine = { ...note, conversation_id: conversationId };
  store.set(pageLinesAtom, (all) => ({ ...all, [locationKey]: pushNote(all[locationKey] ?? [], line) }));
};

/** A post's refusal lines, which an edit can make stale. "Posted …" and "Not confirmed: …" stay: an edit can't undo those. */
const REFUSAL = /^Not (posted|tried):/;

/**
 * The notes once an edit made the refused post stale: its refusal lines go, so the chat stops repeating them. A hand edit
 * always clears them; a chat edit only from a later turn than the refusal's (one in the same turn came before the manager saw it).
 */
export const withoutStaleRefusals = (notes: ReadonlyArray<EditNote>, source: EditSource, after: number) =>
  notes.filter(
    (note) =>
      !(
        note.source === "page" &&
        note.field === "post" &&
        REFUSAL.test(note.text) &&
        (source === "hand" || note.after !== after)
      ),
  );

/** Undo covers the changes since the last post; posting clears it. In memory: a reload starts a fresh stack. */
export const undoAtom = atom<Record<string, Array<UndoEntry>>>({});
/** What changed on the page lately, newest last, for the chat's one collapsed status line. */
export const editNotesAtom = atom<Record<string, Array<EditNote>>>({});
/** The page's own lines (a post's outcome, a clash) kept with their conversation, so a reload still shows them. */
export type PageLine = EditNote & { conversation_id: string | null };
export const pageLinesAtom = atomWithStorage<Record<string, Array<PageLine>>>(
  "qwick-post-page-lines",
  {},
  safeJSONStorage<Record<string, Array<PageLine>>>(),
  {
    getOnInit: true,
  },
);

/** What a toast's Undo does: undo its own edit while it is still the newest, otherwise say where the way back is. */
export const toastUndo = (entry: UndoEntry | null, undoEntry: (entry: UndoEntry | null) => UndoEntry | null) => () => {
  if (!undoEntry(entry)) {
    toast.info(
      "That edit can't be undone from here now. Undo in the footer steps back through newer edits, but not a shift that already posted: change that from the Schedule.",
    );
  }
};

/** Undo is refused when it would take away a shift or role that went out, or may have: its record would outlive it. */
const undoKeepsSentRoles = (before: PostDraft, current: PostDraft) => {
  const restored = restoreDraft(before, current);
  return keepsSentRoles(restored) && !changesSentRoles(current, restored);
};

/** Undo puts back the shifts, never the post's records: what went out cannot be taken back, and its keys settle a retry. */
export const restoreDraft = (before: PostDraft, current: PostDraft): PostDraft => ({
  ...before,
  records: current.records,
});

export const pushUndo = (stack: ReadonlyArray<UndoEntry>, entry: UndoEntry): Array<UndoEntry> =>
  [...stack, entry].slice(-MAX_UNDO);

export const pushNote = <Note extends EditNote>(notes: ReadonlyArray<Note>, note: Note): Array<Note> => {
  const all = [...notes, note];
  if (all.length <= MAX_NOTES) return all;
  // Over the cap, the chat's own edits (which the dock never says) go first, so a hand edit's line is not pushed out.
  const chat = all.findIndex(({ source }) => source === "chat");
  return chat === -1 ? all.slice(1) : all.toSpliced(chat, 1);
};

export type EditOptions = {
  source?: EditSource;
  field?: string;
  cardId?: string;
  undoable?: boolean;
  /** False for a change the page makes itself: it says nothing about who owns a field. */
  tracksOwner?: boolean;
};

/** The draft of a location, and the one way to change it: every change is one undo step and one note for the chat. */
export const usePostDraft = (locationId: number, userId?: number) => {
  const key = String(locationId);
  const draftKey = draftKeyOf(userId, locationId);
  const drafts = useAtomValue(postDraftsAtom);
  const setDrafts = useSetAtom(postDraftsAtom);
  const stacks = useAtomValue(undoAtom);
  const setStacks = useSetAtom(undoAtom);
  const setNotes = useSetAtom(editNotesAtom);
  const store = useStore();
  // Expiry is judged once, at load: a draft open across midnight stays.
  const [expiredAtLoad] = React.useState(() => {
    const found = drafts[draftKey];
    return found && draftExpired(found, Date.now()) ? found : undefined;
  });
  const found = drafts[draftKey] === expiredAtLoad ? undefined : drafts[draftKey];
  const stored = React.useMemo(() => found && currentDraft(found), [found]);
  // Expired drafts, and those of before drafts were per manager, leave the browser's storage for good.
  React.useEffect(() => {
    setDrafts((all) => {
      const live = liveDrafts(all, Date.now());
      const same =
        Object.keys(live).length === Object.keys(all).length && Object.keys(live).every((k) => live[k] === all[k]);
      return same ? all : live;
    });
  }, [setDrafts, draftKey]);
  const draft = React.useMemo(() => stored ?? emptyDraft(locationId), [stored, locationId]);
  const stack = stacks[key] ?? [];
  // The newest draft, for edits made in the same tick as another: each builds on the one before it.
  const latest = React.useRef(draft);
  React.useLayoutEffect(() => {
    latest.current = draft;
  }, [draft]);

  const edit = React.useCallback(
    (label: string, change: (draft: PostDraft) => PostDraft, options: EditOptions = {}) => {
      const before = latest.current;
      const changed = change(before);
      if (changed === before) return before;
      // Bookkeeping edits (records, defaults) say nothing about who owns a field.
      const next =
        options.undoable === false || options.tracksOwner === false
          ? changed
          : withGivenFields(before, withHandSet(before, changed, options.source ?? "hand"), options.source ?? "hand");
      // The one place every hand edit, chat action and undo goes through: a live gig's role or card cannot be changed here.
      const lock = sentLockOf(before, next);
      if (lock) {
        // The chat's actions answer with the refusal themselves.
        if (options.source !== "chat") toast.info(lock);
        return before;
      }
      // Only a manager's or chat's change, or a post's record, counts as touching the draft.
      const touches = options.undoable !== false || options.field === "post";
      const saved = touches ? { ...next, saved_at: Date.now() } : next;
      latest.current = saved;
      setDrafts((all) => ({ ...all, [draftKey]: saved }));
      if (options.undoable !== false) {
        setStacks((all) => ({ ...all, [key]: pushUndo(all[key] ?? [], { label, before }) }));
      }
      const after = store.get(chatMessageCountAtom);
      // A manager's or chat's change to what would post leaves a refused post's reason behind.
      const stales =
        options.undoable !== false && options.tracksOwner !== false && revisionOf(before) !== revisionOf(saved);
      if (stales) {
        setNotes((all) => ({ ...all, [key]: withoutStaleRefusals(all[key] ?? [], options.source ?? "hand", after) }));
      }
      // A change the page makes itself (a row touched, a record kept) has no words for the chat's status line.
      if (label)
        setNotes((all) => ({
          ...all,
          [key]: pushNote(all[key] ?? [], {
            source: options.source ?? "hand",
            field: options.field ?? "card",
            card_id: options.cardId ?? "",
            text: label,
            at: Date.now(),
            after,
          }),
        }));
      return saved;
    },
    [key, draftKey, setDrafts, setStacks, setNotes, store],
  );

  const undo = React.useCallback((): UndoEntry | null => {
    // The stack as it is now: a toast's Undo is called from the render that made it.
    const current = store.get(undoAtom)[key] ?? [];
    const entry = current.at(-1);
    if (!entry) return null;
    if (!undoKeepsSentRoles(entry.before, latest.current)) return null;
    const restored = restoreDraft(entry.before, latest.current);
    latest.current = restored;
    setDrafts((all) => ({ ...all, [draftKey]: { ...restored, saved_at: Date.now() } }));
    setStacks((all) => ({ ...all, [key]: current.slice(0, -1) }));
    return entry;
  }, [key, draftKey, store, setDrafts, setStacks]);

  /** Posting ends what can be undone: a post that went out cannot bring its Post button back. */
  const clearUndo = React.useCallback(() => setStacks((all) => ({ ...all, [key]: [] })), [key, setStacks]);

  /** One line from the page itself, said in the chat after the last message: it changes no field. */
  const addNote = React.useCallback(
    (text: string, field: string, conversationId?: string | null) => {
      sayOnPage(store, key, text, field, conversationId);
    },
    [key, store],
  );

  /** The newest undo step, so a toast can later undo that edit and no other. */
  const newestUndo = React.useCallback((): UndoEntry | null => store.get(undoAtom)[key]?.at(-1) ?? null, [key, store]);
  const undoEntry = React.useCallback(
    (entry: UndoEntry | null): UndoEntry | null => (entry && newestUndo() === entry ? undo() : null),
    [newestUndo, undo],
  );

  /** "Post another": a fresh draft, with nothing to undo. */
  const startOver = React.useCallback(() => {
    const fresh = emptyDraft(locationId);
    latest.current = fresh;
    setDrafts((all) => ({ ...all, [draftKey]: fresh }));
    setStacks((all) => ({ ...all, [key]: [] }));
    setNotes((all) => ({ ...all, [key]: [] }));
  }, [key, draftKey, locationId, setDrafts, setStacks, setNotes]);

  return {
    draft,
    edit,
    addNote,
    undo,
    undoEntry,
    newestUndo,
    clearUndo,
    startOver,
    undoLabel: stack.at(-1)?.label ?? null,
  };
};

/** "Continue draft · Fri, Oct 16 · Prep Cook": the one name for a saved draft on every surface that offers it. */
export const draftChoiceLabel = (draft: PostDraft) => {
  const shift = draft.shifts.find((each) => each.date || each.positions.length > 0) ?? draft.shifts[0];
  const parts = [shift?.date ? dayLabel(shift.date) : null, shift?.positions[0]?.position_label ?? null].filter(
    Boolean,
  );
  return ["Continue draft", ...parts].join(" · ");
};

/** The same path as Start new: a clean card, the old draft one Undo away on the page. */
export const startNewShift = (store: ReturnType<typeof useStore>, userId: number | undefined, locationId: number) => {
  const draftKey = draftKeyOf(userId, locationId);
  const key = String(locationId);
  const before = store.get(postDraftsAtom)[draftKey];
  if (before && !isEmptyDraft(before) && !anySentRole(before)) {
    store.set(undoAtom, (all) => ({
      ...all,
      [key]: pushUndo(all[key] ?? [], { label: "Started a new shift", before }),
    }));
  }
  store.set(postDraftsAtom, (all) => ({ ...all, [draftKey]: emptyDraft(locationId) }));
  sayOnPage(store, key, "You picked New shift", "chip");
};

/** Reopening a saved draft puts it back in hand, so the page does not ask about it again. */
export const resumeDraft = (store: ReturnType<typeof useStore>, userId: number | undefined, locationId: number) => {
  const draftKey = draftKeyOf(userId, locationId);
  store.set(postDraftsAtom, (all) => {
    const draft = all[draftKey];
    return draft ? { ...all, [draftKey]: { ...draft, saved_at: Date.now() } } : all;
  });
};

/** A finished post is a receipt, not a draft: a new shift never opens on it. */
export const clearPostedDraft = (
  store: ReturnType<typeof useStore>,
  userId: number | undefined,
  locationId: number,
) => {
  const draftKey = draftKeyOf(userId, locationId);
  const draft = store.get(postDraftsAtom)[draftKey];
  const records = Object.values(draft?.records ?? {});
  if (records.length === 0 || !records.every((record) => record.outcome === "posted")) return;
  store.set(postDraftsAtom, (all) => ({ ...all, [draftKey]: emptyDraft(locationId) }));
};

/**
 * Marks the roles of this manager's drafts whose gig was cancelled. Each stays a posted record, so it is never posted again,
 * and the receipt on screen says cancelled; reopening the page or New chat clears it as it does any finished post.
 */
export const markCancelled = (
  drafts: Record<string, PostDraft>,
  userId: number | undefined,
  gigIds: ReadonlySet<string>,
  now: number,
): Record<string, PostDraft> => {
  let changed = false;
  const next = Object.fromEntries(
    Object.entries(drafts).map(([key, draft]) => {
      if (!key.startsWith(`${userId ?? 0}:`)) return [key, draft];
      const hits = Object.entries(draft.records ?? {}).filter(
        ([, record]) => isLivePost(record) && record.gig_id != null && gigIds.has(String(record.gig_id)),
      );
      if (hits.length === 0) return [key, draft];
      changed = true;
      const records = {
        ...draft.records,
        ...Object.fromEntries(
          hits.map(([entry, record]) => [entry, { ...record, cancelled: true, cancelled_at: now }]),
        ),
      };
      return [key, { ...draft, records }];
    }),
  );
  return changed ? next : drafts;
};

/** A gig cancelled by the chat or elsewhere: the page stops calling it posted, and so does what the agent is told. */
export const cancelPostedGigs = (
  store: ReturnType<typeof useStore>,
  userId: number | undefined,
  gigIds: ReadonlySet<string>,
) => {
  if (gigIds.size === 0) return;
  const before = store.get(postDraftsAtom);
  const after = markCancelled(before, userId, gigIds, Date.now());
  if (after !== before) {
    store.set(postDraftsAtom, after);
    // For the hand test: the page now says cancelled for these.
    console.warn(`Qwick AI: the Post page marked gig ${[...gigIds].join(", ")} cancelled`);
  }
  const handPosts = draftsAtomFor(String(userId ?? "anonymous"));
  const kept = dropCancelledHandPosts(store.get(handPosts), gigIds);
  if (kept !== store.get(handPosts)) store.set(handPosts, kept);
};

/** New chat: a finished post's receipt ends with the chat that made it, as do the page's lines. A draft in hand stays. */
export const endPostedDraft = (store: ReturnType<typeof useStore>, userId: number | undefined, locationId: number) => {
  clearPostedDraft(store, userId, locationId);
  store.set(editNotesAtom, (all) => ({ ...all, [String(locationId)]: [] }));
};

/** The drafts whose page has opened since this document loaded: any later opening is the manager coming back to it. */
export const openedPagesAtom = atom<ReadonlySet<string>>(new Set<string>());

/** Whether the page opens because the browser reloaded it there, not because the manager or the chat came to it. */
export const openedByReload = (store: ReturnType<typeof useStore>, draftKey: string) => {
  const [load] = performance.getEntriesByType("navigation") as Array<PerformanceNavigationTiming>;
  return (
    load?.type === "reload" &&
    new URL(load.name).pathname === globalThis.location.pathname &&
    !store.get(openedPagesAtom).has(draftKey)
  );
};
